import { createContext, useState, useEffect, useMemo } from 'react';
import { getTheme, buildTokens } from './theme.js';

export const ThemeContext = createContext();

// Convert a hex accent into the three accent tokens used throughout the app
function accentTokens(hex) {
  if (!hex) return {};
  // accentPrimaryBg = hex at ~13% opacity (22 in hex)
  const bg = hex.length === 7 ? hex + '22' : hex.slice(0, 7) + '22';
  // accentPrimaryDark = rough 20% darker — just dim it with opacity overlay
  return {
    accentPrimary: hex,
    accentPrimaryDark: hex + 'cc',
    accentPrimaryBg: bg,
    typing: hex,
  };
}

// Append an 8-bit alpha to a #rrggbb (or #rgb) hex. Returns the input
// unchanged for non-hex colors (rgba/named) so it degrades safely. Hex8 is
// supported on every iOS version we target (color-mix is not).
function withAlpha(color, aa) {
  if (typeof color !== 'string' || color[0] !== '#') return color;
  if (color.length === 4) {
    const r = color[1], g = color[2], b = color[3];
    return `#${r}${r}${g}${g}${b}${b}${aa}`;
  }
  if (color.length === 7) return `${color}${aa}`;
  return color;
}

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => {
    try { return localStorage.getItem('mfml-theme') || 'dark'; }
    catch { return 'dark'; }
  });

  // Custom accent color — null means use the theme's built-in accent
  const [accentColor, setAccentColorState] = useState(() => {
    try { return localStorage.getItem('mfml-accent') || null; }
    catch { return null; }
  });

  const setThemeIdPersisted = (id) => {
    setThemeId(id);
    try { localStorage.setItem('mfml-theme', id); } catch {}
  };

  // Light-theme text color override (Haley, 2026-09-08: "in light modes please
  // make it so the text/font color can be changed"). null = the theme's own ink.
  const [textColor, setTextColorState] = useState(() => {
    try { return localStorage.getItem('mfml-text-color') || null; }
    catch { return null; }
  });
  // Keep the dark composer bar under light themes. Default false: "the text
  // bar can be light" — under a light theme the bar follows the theme.
  const [composerDark, setComposerDarkState] = useState(() => {
    try { return localStorage.getItem('mfml-composer-dark') === '1'; }
    catch { return false; }
  });
  const setTextColor = (hex) => {
    const v = typeof hex === 'string' && /^#[0-9a-f]{6}$/i.test(hex) ? hex : null;
    setTextColorState(v);
    try {
      if (v) localStorage.setItem('mfml-text-color', v);
      else localStorage.removeItem('mfml-text-color');
    } catch {}
  };
  const setComposerDark = (on) => {
    setComposerDarkState(!!on);
    try { localStorage.setItem('mfml-composer-dark', on ? '1' : '0'); } catch {}
  };

  const setAccentColor = (hex) => {
    setAccentColorState(hex || null);
    try {
      if (hex) localStorage.setItem('mfml-accent', hex);
      else localStorage.removeItem('mfml-accent');
    } catch {}
  };

  // Resolve the full theme object, then overlay custom accent if set
  const theme = useMemo(() => {
    const base = getTheme(themeId);
    const withAccent = accentColor ? { ...base, ...accentTokens(accentColor) } : base;
    // The text override applies under LIGHT themes only — that is the ask,
    // and an ink chosen for ivory would vanish on obsidian. textPrimary feeds
    // body color, --ds-ink-hi and every t.textPrimary read, so the transcript
    // recolors without touching a single bubble.
    if (withAccent.light && textColor) return { ...withAccent, textPrimary: textColor, textOverride: textColor };
    return withAccent;
  }, [themeId, accentColor, textColor]);

  // Apply theme as CSS custom properties on <html> so every component can use them.
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    root.style.setProperty('--mfml-bg-deepest', theme.bgDeepest);
    root.style.setProperty('--mfml-bg-base', theme.bgBase);
    root.style.setProperty('--mfml-bg-secondary', theme.bgSecondary);
    root.style.setProperty('--mfml-bg-tertiary', theme.bgTertiary);
    root.style.setProperty('--mfml-bg-input', theme.bgInput);
    root.style.setProperty('--mfml-bg-hover', theme.bgHover);
    root.style.setProperty('--mfml-border-dark', theme.borderDark);
    root.style.setProperty('--mfml-border-medium', theme.borderMedium);
    root.style.setProperty('--mfml-border-light', theme.borderLight);
    root.style.setProperty('--mfml-text-primary', theme.textPrimary);
    root.style.setProperty('--mfml-text-secondary', theme.textSecondary);
    root.style.setProperty('--mfml-text-muted', theme.textMuted);
    root.style.setProperty('--mfml-text-faint', theme.textFaint);
    root.style.setProperty('--mfml-accent-primary', theme.accentPrimary);
    root.style.setProperty('--mfml-accent-primary-dark', theme.accentPrimaryDark);
    root.style.setProperty('--mfml-accent-primary-bg', theme.accentPrimaryBg);
    root.style.setProperty('--mfml-accent-secondary', theme.accentSecondary);
    root.style.setProperty('--mfml-accent-secondary-light', theme.accentSecondaryLight);
    root.style.setProperty('--mfml-accent-success', theme.accentSuccess);
    root.style.setProperty('--mfml-accent-warning', theme.accentWarning);
    root.style.setProperty('--mfml-accent-danger', theme.accentDanger);
    root.style.setProperty('--mfml-accent-gold', theme.accentGold);
    root.style.setProperty('--mfml-online', theme.online);
    root.style.setProperty('--mfml-offline', theme.offline);

    // Derived alpha variants so the "Velvet & Gold" luxe layer (vignette,
    // card glow, candle-glow buttons, gold hairlines, input focus, message
    // identity) follows the ACTIVE theme's own colors — every theme pick gets
    // the same opulent treatment in its palette, not a hardcoded gold/red.
    root.style.setProperty('--mfml-gold-faint', withAlpha(theme.accentGold, '1f'));
    root.style.setProperty('--mfml-gold-glow', withAlpha(theme.accentGold, '38'));
    root.style.setProperty('--mfml-gold-soft', withAlpha(theme.accentGold, '55'));
    root.style.setProperty('--mfml-gold-line', withAlpha(theme.accentGold, '70'));
    root.style.setProperty('--mfml-primary-faint', withAlpha(theme.accentPrimary, '14'));
    root.style.setProperty('--mfml-primary-soft', withAlpha(theme.accentPrimary, '22'));
    root.style.setProperty('--mfml-primary-glow', withAlpha(theme.accentPrimary, '66'));
    root.style.setProperty('--mfml-primary-line', withAlpha(theme.accentPrimary, '8c'));
    root.style.setProperty('--mfml-secondary-soft', withAlpha(theme.accentSecondary, '4d'));

    // Design-system tokens (v5 "Candlelit Glass") — the full semantic layer.
    // Everything in src/index.css and migrated inline styles reads these, so
    // the active theme (and custom accent) re-skins the entire app.
    for (const [k, v] of Object.entries(buildTokens(theme))) {
      root.style.setProperty(k, v);
    }

    body.style.background = theme.bgDeepest;
    body.style.color = theme.textPrimary;
    root.style.background = theme.bgDeepest;

    // Light-theme flag — index.css gates the dark-only ornament (film grain,
    // wine vignette, gold-on-obsidian damask) behind :not(.theme-light) and
    // swaps in warm, luminous equivalents under .theme-light. Also flips the
    // native form-control palette (date pickers, scrollbars) via color-scheme.
    root.classList.toggle('theme-light', !!theme.light);
    root.style.colorScheme = theme.light ? 'light' : 'dark';
  }, [theme]);

  const value = {
    themeId,
    setThemeId: setThemeIdPersisted,
    theme,
    accentColor,
    setAccentColor,
    textColor,
    setTextColor,
    composerDark,
    setComposerDark,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
